import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        totp_code: totpCode || null
      });

      toast.success("Login successful!");
      onLogin(response.data.user, response.data.access_token);
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.data?.detail === "TOTP code required") {
        setNeedsTotp(true);
        toast.info("Please enter your 6-digit authentication code");
      } else {
        toast.error(error.response?.data?.detail || "Login failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* ── Left Panel: Branding ── */}
      <div
        className="hidden lg:flex flex-col justify-between relative overflow-hidden"
        style={{
          width: "55%",
          background: "linear-gradient(160deg, #eef2f7 0%, #dce8e4 40%, #c9ddd4 70%, #e0ebe6 100%)",
        }}
        data-testid="login-left-panel"
      >
        {/* Subtle tech pattern overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0.06,
            backgroundImage: `
              radial-gradient(circle at 20% 80%, #2d5a27 1px, transparent 1px),
              radial-gradient(circle at 80% 20%, #2d5a27 1px, transparent 1px),
              radial-gradient(circle at 50% 50%, #2d5a27 0.5px, transparent 0.5px)
            `,
            backgroundSize: "60px 60px, 80px 80px, 40px 40px",
          }}
        />

        {/* Brand content — centered in the panel */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
          <div>
            {/* SaakhSetu Logo */}
            <img
              src="/saakhsetu-logo.png"
              alt="SaakhSetu"
              style={{ height: "120px" }}
              data-testid="login-saakhsetu-logo"
            />
            <div className="flex items-center gap-2 mt-4 justify-end">
              <span style={{ fontSize: "14px", color: "#4a5568", letterSpacing: "0.2px", fontWeight: 400 }}>a product of</span>
              <img
                src="/arbix-logo.png"
                alt="Arbix AI Solutions"
                style={{ height: "28px" }}
                data-testid="login-arbix-logo"
              />
            </div>

            {/* Tagline */}
            <h2
              className="mt-8"
              style={{
                fontSize: "21px",
                fontWeight: 600,
                color: "#2d5a27",
                lineHeight: 1.35,
                letterSpacing: "-0.2px",
                whiteSpace: "nowrap",
              }}
              data-testid="login-tagline"
            >
              Faster, Fairer, Agricultural Credit Appraisal
            </h2>
          </div>
        </div>

        {/* Bottom: Yogyank footer */}
        <div
          className="relative z-10 px-12 pb-8 flex items-center gap-2.5"
          data-testid="login-yogyank-footer"
        >
          <span style={{ fontSize: "13px", color: "#5a6d63", letterSpacing: "0.3px", fontWeight: 500 }}>
            Scoring Engine Powered by
          </span>
          <img
            src="/yogyank-logo.png"
            alt="Yogyank"
            style={{ height: "22px" }}
            data-testid="login-yogyank-logo"
          />
        </div>
      </div>

      {/* ── Right Panel: Login Form ── */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-16"
        style={{ background: "#f9fafb" }}
      >
        {/* Mobile-only branding */}
        <div className="lg:hidden flex flex-col items-center mb-8">
          <img src="/saakhsetu-logo.png" alt="SaakhSetu" style={{ height: "54px" }} />
          <div className="flex items-center gap-1.5 mt-2">
            <span style={{ fontSize: "12px", color: "#6b7280" }}>a product of</span>
            <img src="/arbix-logo.png" alt="Arbix AI Solutions" style={{ height: "20px" }} />
          </div>
        </div>

        {/* Form Card */}
        <div
          className="w-full max-w-md"
          style={{
            background: "#ffffff",
            borderRadius: "16px",
            boxShadow: "0 4px 32px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)",
            padding: "48px 40px 40px",
          }}
          data-testid="login-card"
        >
          <h1
            className="text-center mb-8"
            style={{
              fontSize: "32px",
              fontWeight: 600,
              color: "#2d5a27",
              letterSpacing: "-0.5px",
            }}
            data-testid="login-heading"
          >
            Sign-In
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                placeholder="agent@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                style={{ borderRadius: "8px", fontSize: "14px", borderColor: "#d1d5db" }}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                style={{ borderRadius: "8px", fontSize: "14px", borderColor: "#d1d5db" }}
              />
            </div>

            {needsTotp && (
              <div className="space-y-1.5">
                <Label htmlFor="totp" style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>Authentication Code</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={totpCode}
                    onChange={(value) => setTotpCode(value)}
                    data-testid="login-totp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 font-semibold text-white"
              disabled={loading}
              data-testid="login-submit-button"
              style={{
                background: "#3b6e2a",
                borderRadius: "8px",
                fontSize: "15px",
                letterSpacing: "0.2px",
                marginTop: "20px",
              }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <img src="/saakhsetu-loader.webp" alt="" style={{ width: 20, height: 20 }} />
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Enterprise tagline */}
          <p
            className="text-center mt-8"
            style={{
              fontSize: "12px",
              color: "#4b5563",
              lineHeight: 1.6,
              fontWeight: 500,
            }}
            data-testid="login-enterprise-tagline"
          >
            Enterprise Grade, AI-Powered Loan Appraisal &amp; Scoring Platform for Agricultural Loans
          </p>

          {/* Register link */}
          <div className="text-center mt-4" data-testid="login-register-link">
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Don't have an account? </span>
            <Link
              to="/register"
              style={{ fontSize: "13px", fontWeight: 600, color: "#3b6e2a" }}
              data-testid="register-link"
            >
              Register here
            </Link>
          </div>
        </div>

        {/* Mobile-only Yogyank footer */}
        <div className="lg:hidden flex items-center mt-6 gap-2" data-testid="login-yogyank-footer-mobile">
          <span style={{ fontSize: "11px", color: "#9ca3af" }}>Scoring Engine Powered by</span>
          <img src="/yogyank-logo.png" alt="Yogyank" style={{ height: "16px" }} />
        </div>
      </div>
    </div>
  );
};

export default Login;
